import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams, useNavigate } from "react-router";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { getCampaignSearchFields } from '../utils/campaignSearchUtils';
import { db } from '../firebase';
import { useUser } from '../userContext';
import Loading from "../common/DefaultLoading";
import AlertError from '../dashboard/PermissionDenied';
import { updateCampaignStep } from '../common/Database';
import FormComponent from './Form';
import {
  ArrowTopRightOnSquareIcon,
  DevicePhoneMobileIcon,
  ChevronLeftIcon,
  ArrowPathIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { ComputerDesktopIcon, EllipsisHorizontalIcon, PuzzlePieceIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import ModalBasic from '../components/ModalBasic';
import { normalizeCampaignStatus } from '../data/campaignStatus';
import { getQuizRushPublishError } from '../data/quizRushSchema';
import { getCompleteListPublishError } from '../data/completeListSchema';
import { gameTypes } from '../data/GameTypes';
import { PRESERVED_OPTION_FIELDS, computeSwitchImpact } from './gameTypeSwitchImpact';

export default function Editor() {
  const params = useParams();
  const navigate = useNavigate();
  const { user, loading, businessProfile } = useUser();
  const [isLoading, setIsLoading] = useState(true); 
  const [gameData, setGameData] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  // True while the save request is in flight — the button label flips to
  // "Saving…" so the click feels acknowledged even on slow networks.
  const [isSaving, setIsSaving] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState(null); 
  const [errors, setErrors] = useState({}); 
  const formRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(0);
  const previewIframeRef = useRef(null);
  const [previewDisplayMode, setPreviewDisplayMode] = useState('phone'); // 'phone' | 'desktop'
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const phonePreviewContainerRef = useRef(null);
  const editorRootRef = useRef(null);
  // Overflow menu in the header (Switch game type lives here, not in
  // the sidebar — it's a re-base of the campaign, not a property edit).
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef(null);
  // Form exposes { setActiveSection } here so the header menu can route
  // to the game-type picker in the right panel.
  const formApiRef = useRef(null);
  // Rename modal (internal name only — public title lives in Start Screen)
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const previewTargetOrigin = typeof window !== 'undefined' ? window.location.origin : '*';

  // Mobile preview drawer
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const mobilePreviewIframeRef = useRef(null);
  const [mobileIframeReady, setMobileIframeReady] = useState(false);
  const [mobileSaveSuccess, setMobileSaveSuccess] = useState(false);
  const savedTimerRef = useRef(null);

  // Close header overflow menu on outside click / Escape
  useEffect(() => {
    if (!headerMenuOpen) return;
    const onDocClick = (e) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) {
        setHeaderMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setHeaderMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [headerMenuOpen]);




  const retryCountRef = useRef(0);

  const fetchCampaign = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (params.id) {
        const docRef = doc(db, "campaign", params.id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const campaignData = docSnap.data();

          if (campaignData.companyId !== businessProfile.id) {
            console.error('Campaign belongs to a different workspace');
            setError("You don't have access to this game. It belongs to a different account.");
            setIsLoading(false);
            return;
          }
          retryCountRef.current = 0;
          setGameData(campaignData);
        } else {
          console.error('Campaign not found');
          setError("Campaign not found.");
        }
      } else {
        console.error("params.id is missing");
        setError("Campaign not found.");
      }
    } catch (err) {
      console.error('Error fetching campaign:', err);
      // Auto-retry once on transient errors (stale auth token, network hiccup)
      if (retryCountRef.current < 1) {
        retryCountRef.current += 1;
        console.log('Auto-retrying campaign fetch...');
        setTimeout(() => fetchCampaign(), 1000);
        return;
      }
      setError("An error occurred while fetching the campaign.");
    } finally {
      setIsLoading(false);
    }
  }, [params.id, businessProfile?.id]);

  useEffect(() => {
    if (!user || !params.id || !businessProfile?.id) return;
    fetchCampaign();
  }, [params.id, user, businessProfile?.id, fetchCampaign]);

  // Editor owns scrolling on both desktop and mobile: disable outer layout
  // scroll/padding while mounted so the inner Form is the only scroller.
  // On iOS Safari this stops scroll-chain from bouncing the whole app frame
  // (the URL/status chrome animating in/out as the page body scrolls). The
  // editor's mobile container uses h-[100dvh], which already shrinks correctly
  // when the soft keyboard opens (iOS 16.4+).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const appScrollRoot = document.querySelector('[data-app-scroll-root]');
    const contentWrapper = appScrollRoot?.firstElementChild;
    const htmlEl = document.documentElement;
    const bodyEl = document.body;

    const prevRootOverflowY = appScrollRoot?.style.overflowY;
    const prevRootOverflowX = appScrollRoot?.style.overflowX;
    const prevRootOverscroll = appScrollRoot?.style.overscrollBehavior;
    const prevWrapperPadding = contentWrapper?.style.padding;
    const prevWrapperHeight = contentWrapper?.style.height;
    const prevWrapperMaxWidth = contentWrapper?.style.maxWidth;
    const prevHtmlOverflow = htmlEl.style.overflow;
    const prevBodyOverflow = bodyEl.style.overflow;
    const prevHtmlOverscroll = htmlEl.style.overscrollBehavior;
    const prevBodyOverscroll = bodyEl.style.overscrollBehavior;
    const prevHtmlHeight = htmlEl.style.height;
    const prevBodyHeight = bodyEl.style.height;

    if (appScrollRoot instanceof HTMLElement) {
      appScrollRoot.style.overflowY = 'hidden';
      appScrollRoot.style.overflowX = 'hidden';
      appScrollRoot.style.overscrollBehavior = 'none';
    }
    if (contentWrapper instanceof HTMLElement) {
      contentWrapper.style.padding = '0';
      contentWrapper.style.height = '100%';
      contentWrapper.style.maxWidth = '100%';
    }
    // iOS Safari can still scroll-chain to page; hard-lock page scroll while editor is mounted.
    htmlEl.style.overflow = 'hidden';
    bodyEl.style.overflow = 'hidden';
    htmlEl.style.overscrollBehavior = 'none';
    bodyEl.style.overscrollBehavior = 'none';
    htmlEl.style.height = '100%';
    bodyEl.style.height = '100%';

    return () => {
      if (appScrollRoot instanceof HTMLElement) {
        appScrollRoot.style.overflowY = prevRootOverflowY || '';
        appScrollRoot.style.overflowX = prevRootOverflowX || '';
        appScrollRoot.style.overscrollBehavior = prevRootOverscroll || '';
      }
      if (contentWrapper instanceof HTMLElement) {
        contentWrapper.style.padding = prevWrapperPadding || '';
        contentWrapper.style.height = prevWrapperHeight || '';
        contentWrapper.style.maxWidth = prevWrapperMaxWidth || '';
      }
      htmlEl.style.overflow = prevHtmlOverflow || '';
      bodyEl.style.overflow = prevBodyOverflow || '';
      htmlEl.style.overscrollBehavior = prevHtmlOverscroll || '';
      bodyEl.style.overscrollBehavior = prevBodyOverscroll || '';
      htmlEl.style.height = prevHtmlHeight || '';
      bodyEl.style.height = prevBodyHeight || '';
    };
  }, []);

  // Phone preview now fills available space; games are responsive so no fixed viewport scaling.

  useEffect(() => {
    if (!businessProfile?.id) {
      return;
    }

    const companyId = businessProfile.id;

    try {
      const docRef = doc(db, "workspace", companyId);
      const unsubscribe = onSnapshot(
        docRef,
        {
          includeMetadataChanges: true
        },
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            if (!docSnapshot.metadata.hasPendingWrites) {
              // Send company update to active preview iframe
              const companyMessage = {
                type: 'COMPANY_UPDATE',
                companyData: docSnapshot.data()
              };
              
              if (previewIframeRef.current?.contentWindow) {
                previewIframeRef.current.contentWindow.postMessage(companyMessage, previewTargetOrigin);
              }
              if (mobilePreviewIframeRef.current?.contentWindow) {
                mobilePreviewIframeRef.current.contentWindow.postMessage(companyMessage, previewTargetOrigin);
              }
            }
          }
        },
        (error) => {
          console.error("Error listening to company changes:", error);
        }
      );

      return () => {
        unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up listener:', error);
    }
  }, [businessProfile?.id, previewTargetOrigin]);

  // Push every gameData change to the preview iframe (debounced to coalesce rapid edits)
  const previewSyncTimerRef = useRef(null);
  const gameDataRef = useRef(gameData);
  gameDataRef.current = gameData;
  useEffect(() => {
    const hasDesktop = previewIframeRef.current?.contentWindow;
    const hasMobile = mobilePreviewIframeRef.current?.contentWindow;
    // Skip the initial render (iframe hasn't loaded yet; handleIframeLoad handles first sync)
    if ((!hasDesktop && !hasMobile) || isPreviewLoading) return;
    // Debounce to coalesce rapid sequential changes (e.g. color picker drag)
    clearTimeout(previewSyncTimerRef.current);
    previewSyncTimerRef.current = setTimeout(() => {
      const msg = {
        type: 'PREVIEW_UPDATE',
        gameData: { ...gameDataRef.current, id: params.id || 'preview' }
      };
      if (previewIframeRef.current?.contentWindow) {
        previewIframeRef.current.contentWindow.postMessage(msg, previewTargetOrigin);
      }
      if (mobilePreviewIframeRef.current?.contentWindow) {
        mobilePreviewIframeRef.current.contentWindow.postMessage(msg, previewTargetOrigin);
      }
    }, 30);
    return () => clearTimeout(previewSyncTimerRef.current);
  }, [gameData, params.id, previewTargetOrigin, isPreviewLoading]);

  const handleOptionsUpdate = (updatedOptions) => {
    setUnsavedChanges(true);
    setGameData(prev => {
      const newOptions = { ...prev.options, ...updatedOptions };
      return { ...prev, options: newOptions };
    });
  };

  const reloadPreview = () => {
    setIsPreviewLoading(true);
    setIframeKey(k => k + 1);
  };

  const validateForm = () => {
    const form = formRef.current;
    if (!form?.reportValidity()) {
      console.error('Validation failed, not saving');
      return false;
    }
    if (gameData?.gameType === 'quizrush') {
      const err = getQuizRushPublishError(gameData?.options);
      if (err) {
        setErrors((e) => ({ ...e, quizrush: err + ' Add or complete questions in Game Assets.' }));
        return false;
      }
    }
    setErrors((e) => ({ ...e, quizrush: undefined }));
    if (gameData?.gameType === 'completelist') {
      const err = getCompleteListPublishError(gameData?.options);
      if (err) {
        setErrors((e) => ({ ...e, completelist: err + ' Add items in Game Assets.' }));
        return false;
      }
    }
    setErrors((e) => ({ ...e, completelist: undefined }));
    return true;
  };

  // Quiz Rush validation error as floating toast
  const quizrushError = errors?.quizrush;
  useEffect(() => {
    if (quizrushError) {
      toast.error(quizrushError, {
        toastId: 'quizrush-publish-error',
        position: 'top-center',
        autoClose: 6000,
        hideProgressBar: false,
      });
    } else {
      toast.dismiss('quizrush-publish-error');
    }
  }, [quizrushError]);

  // Complete the List validation error as floating toast
  const completelistError = errors?.completelist;
  useEffect(() => {
    if (completelistError) {
      toast.error(completelistError, {
        toastId: 'completelist-publish-error',
        position: 'top-center',
        autoClose: 6000,
        hideProgressBar: false,
      });
    } else {
      toast.dismiss('completelist-publish-error');
    }
  }, [completelistError]);

  // Sanitize options before saving to prevent undefined values in Firestore
  const sanitizeOptions = (data) => {
    const sanitized = JSON.parse(JSON.stringify(data, (key, value) => {
      // Replace undefined with empty string for specific CTA fields
      if ((key === 'cta_text' || key === 'cta_link') && value === undefined) {
        return '';
      }
      // Remove other undefined values
      return value === undefined ? null : value;
    }));
    
    // Ensure CTA fields always exist as empty strings if undefined
    if (sanitized.options) {
      sanitized.options.cta_text = sanitized.options.cta_text || '';
      sanitized.options.cta_link = sanitized.options.cta_link || '';
    }
    // Quiz Rush: leaderboard is first-score-only; ensure we persist false
    if (sanitized.gameType === 'quizrush' && sanitized.options?.leaderboard) {
      sanitized.options.leaderboard = {
        ...sanitized.options.leaderboard,
        allowMultipleAttempts: false,
      };
    }
    return sanitized;
  };

  const saveOptionsToDB = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      await updateCampaignStep(params.id, 3);
      const docRef = doc(db, "campaign", params.id);
      const sanitizedData = sanitizeOptions(gameData);
      const { _searchInternalName, _searchTitle } = getCampaignSearchFields(sanitizedData);
      // Add editedInEditor flag and timestamp when saving from editor
      await setDoc(docRef, {
        ...sanitizedData,
        _searchInternalName,
        _searchTitle,
        editedInEditor: true,
        lastEditedAt: new Date().toISOString()
      }, { merge: true });
      setIsPreviewLoading(true);
      setMobileIframeReady(false);
      setIframeKey(prev => prev + 1);
      // Flip unsaved → saved together so the idle "Save" state never
      // appears between them.
      clearTimeout(savedTimerRef.current);
      setUnsavedChanges(false);
      setShowSavedIndicator(true);
      setMobileSaveSuccess(true);
      savedTimerRef.current = setTimeout(() => {
        setShowSavedIndicator(false);
        setMobileSaveSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error saving options to database:', error);
      toast.error('Changes couldn\'t be saved. Check your connection and try again.', {
        position: 'top-center',
        autoClose: false,
        toastId: 'save-error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Game-type-switch confirmation. computeSwitchImpact tells us what the user
  // would lose. We only block on `destructive` impacts (authored content like
  // quiz questions or list items); `warning` impacts (custom artwork that
  // doesn't translate) flip silently to keep the common case fast.
  const [pendingSwitchType, setPendingSwitchType] = useState(null);
  const switchImpact = useMemo(() => {
    if (!pendingSwitchType || !gameData) return [];
    return computeSwitchImpact({
      currentGameTypeId: gameData.gameType,
      currentOptions: gameData.options || {},
      newGameTypeId: pendingSwitchType.id,
    });
  }, [pendingSwitchType, gameData]);
  const hasDestructiveImpact = switchImpact.some((i) => i.severity === 'destructive');

  const performSwitchGameType = async (newType) => {
    const cur = gameData?.options || {};
    const preserved = {};
    for (const k of PRESERVED_OPTION_FIELDS) {
      if (cur[k] !== undefined) preserved[k] = cur[k];
    }
    let newOptions = { ...newType.options, ...preserved };
    if (newType.id === 'quizrush' && newOptions.leaderboard) {
      newOptions.leaderboard = { ...newOptions.leaderboard, allowMultipleAttempts: false };
    }
    const newGameData = {
      ...gameData,
      gameType: newType.id,
      gameTypeDisplayValue: newType.name,
      options: newOptions,
    };
    setGameData(newGameData);
    try {
      const docRef = doc(db, 'campaign', params.id);
      const sanitized = sanitizeOptions(newGameData);
      const { _searchInternalName, _searchTitle } = getCampaignSearchFields(sanitized);
      await setDoc(docRef, {
        ...sanitized,
        _searchInternalName,
        _searchTitle,
        editedInEditor: true,
        lastEditedAt: new Date().toISOString(),
      }, { merge: true });
      setIsPreviewLoading(true);
      setMobileIframeReady(false);
      setIframeKey(k => k + 1);
    } catch (error) {
      console.error('Error switching game type:', error);
    }
  };

  const handleRenameSubmit = async (e) => {
    e?.preventDefault?.();
    const next = (renameValue || '').trim();
    if (!next || next === gameData.internalName) {
      setRenameModalOpen(false);
      return;
    }
    setRenameSaving(true);
    try {
      const docRef = doc(db, 'campaign', params.id);
      await setDoc(docRef, { internalName: next }, { merge: true });
      setGameData((prev) => ({ ...prev, internalName: next }));
      setRenameModalOpen(false);
    } catch (err) {
      console.error('Failed to rename campaign:', err);
      toast.error("Couldn't rename. Try again.", { position: 'top-center' });
    } finally {
      setRenameSaving(false);
    }
  };

  const handleSwitchGameType = async (newType) => {
    if (!newType || newType.id === gameData?.gameType) return;
    const impact = computeSwitchImpact({
      currentGameTypeId: gameData?.gameType,
      currentOptions: gameData?.options || {},
      newGameTypeId: newType.id,
    });
    if (impact.some((i) => i.severity === 'destructive')) {
      setPendingSwitchType(newType);
      return;
    }
    await performSwitchGameType(newType);
  };

  // Cmd+S / Ctrl+S keyboard shortcut to save
  const saveRef = useRef(saveOptionsToDB);
  saveRef.current = saveOptionsToDB;
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Warn on browser back / reload / tab close when there are unsaved changes
  useEffect(() => {
    if (!unsavedChanges) return;
    const handler = (e) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [unsavedChanges]);

  const closeEditor = () => {
    if (unsavedChanges) {
      setTimeout(() => {
        setShowAlert(true);
      }, 300);
    } else {
      navigate(`/studio/campaign/${params.id}`, { state: { from: '/studio/campaigns' } });
    }
  };

  const handleModalConfirm = (isOpen) => {
    if (!isOpen) {
      if (unsavedChanges) {
        setTimeout(() => {
          navigate(`/studio/campaign/${params.id}`, { state: { from: '/studio/campaigns' } });
        }, 300);
      }
    }
    setShowAlert(isOpen);
  };

  // Clean URL for "open in new tab" – loads saved game from DB. No encoded payload.
  const getPreviewUrl = () => `/p/${params.id}?preview=true`;

  const handleIframeLoad = (e) => {
    setIsPreviewLoading(false);
    const msg = {
      type: 'PREVIEW_UPDATE',
      gameData: { ...gameData, id: params.id || 'preview' }
    };
    // Send to whichever iframe just loaded
    e?.target?.contentWindow?.postMessage(msg, previewTargetOrigin);
  };

  const previewUrl = getPreviewUrl();

  const editorFormOptions = gameData?.options ?? {};
  const campaignStatus = normalizeCampaignStatus(gameData?.status);
  const isLive = campaignStatus === 'published';

  if (loading || isLoading) return <Loading />;

  if (error) {
    return <AlertError text={error} onRetry={() => { retryCountRef.current = 0; fetchCampaign(); }} />;
  }

  return (
    <div ref={editorRootRef} className="admin-theme min-h-[100dvh] md:min-h-[100vh] h-auto max-h-none md:h-auto md:max-h-none overflow-hidden md:overflow-visible flex flex-col md:flex-row gch-layout-bg">
      {/* Desktop Layout - Hidden on Mobile */}
      <div className="hidden md:flex flex-col flex-1 min-h-0 min-w-0">
        {/* Top bar */}
        <div className="px-3 py-2 border-b border-[var(--admin-border)] flex items-center justify-between gap-3 gch-bg-card min-h-[48px] flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={closeEditor}
              className="p-1.5 rounded-lg transition-all duration-200 gch-text-muted hover:gch-text-primary hover:bg-white/10 flex-shrink-0"
              title="Back to campaign"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <span className="text-xs gch-text-muted truncate">
              {gameData.internalName || gameData.options?.title || 'Untitled Game'}
            </span>
            {/* Campaign-level overflow menu — destructive / infrequent
                actions live here, off the editing surface */}
            <div className="relative" ref={headerMenuRef}>
              <button
                type="button"
                onClick={() => setHeaderMenuOpen((v) => !v)}
                className="p-1 rounded-md transition-colors duration-200 gch-text-muted hover:gch-text-primary hover:bg-white/10 flex items-center"
                title="Campaign actions"
                aria-haspopup="menu"
                aria-expanded={headerMenuOpen}
              >
                <EllipsisHorizontalIcon className="w-4 h-4" />
              </button>
              {headerMenuOpen && (
                <div
                  role="menu"
                  className="absolute left-0 top-full mt-1 min-w-[14rem] z-50 rounded-lg gch-bg-card border border-[var(--admin-border)] shadow-lg p-1"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setHeaderMenuOpen(false);
                      setRenameValue(gameData.internalName || '');
                      setRenameModalOpen(true);
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] gch-text-primary hover:bg-white/10 transition-colors"
                  >
                    <PencilSquareIcon className="w-4 h-4 flex-shrink-0" />
                    Rename
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setHeaderMenuOpen(false);
                      formApiRef.current?.setActiveSection?.('gameType');
                    }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] gch-text-primary hover:bg-white/10 transition-colors"
                  >
                    <PuzzlePieceIcon className="w-4 h-4 flex-shrink-0" />
                    Switch game type
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={saveOptionsToDB}
              disabled={(!unsavedChanges && !showSavedIndicator) || isSaving}
              className={`relative inline-flex items-center justify-center w-[7.5rem] h-[1.875rem] rounded-lg text-xs font-semibold transition-colors duration-300 ${
                isSaving
                  ? 'bg-[var(--admin-cta-solid)]/80 text-white cursor-progress'
                  : unsavedChanges
                    ? 'bg-[var(--admin-cta-solid)] text-white hover:brightness-110 shadow-sm ring-2 ring-[var(--admin-cta-solid)]/30'
                    : showSavedIndicator
                      ? 'bg-green-500/15 text-green-400 border border-green-400/30'
                      : 'gch-input-ghost gch-text-muted cursor-default border border-[var(--admin-border)]'
              }`}
              title={isSaving ? 'Saving…' : unsavedChanges ? 'Save changes (⌘S)' : 'No changes to save'}
            >
              {/* Saving state — spinner + label, shown only while the request is in flight */}
              <span className={`absolute inset-0 inline-flex items-center justify-center gap-1.5 transition-opacity duration-200 ${isSaving ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"></path>
                </svg>
                Saving…
              </span>
              {/* Unsaved state */}
              <span className={`absolute inset-0 inline-flex items-center justify-center gap-1.5 transition-opacity duration-200 ${unsavedChanges && !isSaving ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Save changes
              </span>
              {/* Saved state */}
              <span className={`absolute inset-0 inline-flex items-center justify-center gap-1.5 transition-opacity duration-200 ${showSavedIndicator && !unsavedChanges && !isSaving ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Saved
              </span>
              {/* Idle state */}
              <span className={`absolute inset-0 inline-flex items-center justify-center transition-opacity duration-200 ${!unsavedChanges && !showSavedIndicator && !isSaving ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                Save
              </span>
            </button>
            <div className="flex rounded-lg p-0.5 gch-input-ghost border border-[var(--admin-border)]" role="tablist" aria-label="Preview display">
              <button
                type="button"
                onClick={() => { setPreviewDisplayMode('phone'); setIsNavCollapsed(false); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  previewDisplayMode === 'phone'
                    ? 'bg-white/15 gch-text-primary'
                    : 'gch-text-muted hover:gch-text-secondary'
                }`}
                title="Phone view"
                aria-pressed={previewDisplayMode === 'phone'}
              >
                <DevicePhoneMobileIcon className="w-3.5 h-3.5" />
                Phone
              </button>
              <button
                type="button"
                onClick={() => { setPreviewDisplayMode('desktop'); setIsNavCollapsed(true); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  previewDisplayMode === 'desktop'
                    ? 'bg-white/15 gch-text-primary'
                    : 'gch-text-muted hover:gch-text-secondary'
                }`}
                title="Desktop view"
                aria-pressed={previewDisplayMode === 'desktop'}
              >
                <ComputerDesktopIcon className="w-3.5 h-3.5" />
                Desktop
              </button>
            </div>
          </div>
        </div>

        {/* Content row: Form + Preview */}
        <div className="flex flex-1 min-h-0 min-w-0">
          {/* Left Panel - Form only */}
          <div className="flex flex-col flex-1 min-h-0 min-w-[28rem] border-r border-[var(--admin-border)]">
            <div className="flex-1 overflow-hidden min-w-0">
              <div className="h-full overflow-y-auto overflow-x-hidden min-w-0 studio-scrollbar">
                <FormComponent
                  options={editorFormOptions}
                  game={gameData}
                  onOptionsUpdate={handleOptionsUpdate}
                  onRequestPreviewReload={reloadPreview}
                  params={params}
                  validateForm={validateForm}
                  errors={errors}
                  formRef={formRef}
                  navCollapsed={isNavCollapsed}
                  onNavCollapsedChange={previewDisplayMode === 'phone' ? () => setIsNavCollapsed((c) => !c) : undefined}
                  onSwitchGameType={handleSwitchGameType}
                  allGameTypes={gameTypes}
                  formApiRef={formApiRef}
                />
              </div>
            </div>
          </div>

          {/* Right Panel - Preview. In phone mode cap height to viewport so preview fits without scroll. */}
          <div className={`flex flex-col min-h-0 border-l border-[var(--admin-border)] transition-[width] duration-300 overflow-hidden ${
            previewDisplayMode === 'phone' ? 'w-[calc((100vh-4rem)*9/16)] flex-shrink-0 max-h-[calc(100vh-4rem)]' : 'flex-1 min-w-0'
          }`}>
          {/* Preview Content - phone: aspect-ratio frame; desktop: fill */}
          <div
            className={`flex-1 min-h-0 relative overflow-hidden flex ${previewDisplayMode === 'phone' ? 'items-center justify-center' : ''}`}
            style={{ background: 'var(--admin-bg-primary)' }}
          >
            {isPreviewLoading && (
              <div className="absolute top-0 left-0 right-0 z-10 flex flex-col items-center">
                <div className="w-full h-0.5 bg-white/10 overflow-hidden">
                  <div className="h-full bg-[var(--admin-accent)] animate-[preview-progress_1.2s_ease-in-out_infinite]" />
                </div>
                <div className="mt-2.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm flex items-center gap-2">
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin text-[var(--admin-accent)]" />
                  <span className="text-[11px] font-medium gch-text-primary">Refreshing</span>
                </div>
              </div>
            )}
            <div ref={phonePreviewContainerRef} className={`min-h-0 flex flex-col overflow-hidden ${
              previewDisplayMode === 'phone' ? 'w-full aspect-[9/16] max-h-full' : 'w-full h-full'
            }`}>
              <div className={`flex-1 min-h-0 overflow-hidden relative ${
                previewDisplayMode === 'phone' ? 'border border-[var(--admin-border)] shadow-xl' : ''
              }`}>
                {/* Skeleton behind the iframe - shows while it boots so the user
                    isn't staring at empty grey. Fades out once the iframe loads. */}
                <div
                  className={`absolute inset-0 transition-opacity duration-300 pointer-events-none ${isPreviewLoading ? 'opacity-100' : 'opacity-0'}`}
                  aria-hidden="true"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))' }}
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/5 animate-pulse" />
                    <div className="w-32 h-3 rounded bg-white/5 animate-pulse" />
                    <div className="w-24 h-2.5 rounded bg-white/5 animate-pulse" />
                    <div className="w-28 h-9 rounded-lg bg-white/5 animate-pulse mt-2" />
                  </div>
                </div>
                <iframe
                  key={iframeKey}
                  ref={previewIframeRef}
                  src={`/p/${params.id}?preview=true`}
                  className={`w-full h-full min-h-0 border-0 block relative ${isPreviewLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300 ease-out'}`}
                  style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
                  title="Game Preview"
                  allowFullScreen
                  onLoad={handleIframeLoad}
                />
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Mobile Layout - position:fixed inset-0 + 100dvh so the keyboard
          overlays the bottom of the editor instead of resizing the chrome.
          iOS auto-scrolls the inner form scroller to keep the focused input
          visible above the keyboard. */}
      <div
        className="md:hidden flex flex-col overflow-hidden safe-area-top gch-bg-card fixed inset-0"
        style={{ height: '100dvh' }}
      >
        {/* Mobile Header - fixed-height bar with thumb-friendly tap targets */}
        <div className="shrink-0 px-2.5 border-b border-[var(--admin-border)] flex items-center justify-between gap-2 gch-bg-card h-14">
          <div className="flex items-center gap-1 min-w-0">
            <button
              onClick={closeEditor}
              className="p-2.5 -ml-1 rounded-lg transition-colors gch-text-muted hover:gch-text-primary hover:bg-white/10 shrink-0"
              title="Back to campaign"
              aria-label="Back to campaign"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <span className="text-[13px] font-medium gch-text-secondary truncate">
              {gameData.internalName || gameData.options?.title || 'Untitled Game'}
            </span>
          </div>

          {/* Right: Preview + Save */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => { setMobilePreviewOpen(true); if (!mobileIframeReady) setIsPreviewLoading(true); }}
              className="btn-admin-outline inline-flex items-center gap-1.5 rounded-lg px-3 h-10 text-xs font-semibold"
              title="Open preview"
              aria-label="Open preview"
            >
              <EyeIcon className="w-4 h-4" />
              <span className="hidden xs:inline">Preview</span>
            </button>
            <button
              type="button"
              onClick={saveOptionsToDB}
              disabled={!unsavedChanges && !mobileSaveSuccess}
              className={`relative inline-flex items-center justify-center w-20 h-10 rounded-lg text-xs font-semibold transition-all duration-300 ${
                mobileSaveSuccess
                  ? 'bg-green-500/15 text-green-400 border border-green-400/30'
                  : unsavedChanges
                    ? 'bg-[var(--admin-cta-solid)] text-white hover:brightness-110 shadow-sm'
                    : 'gch-input-ghost gch-text-muted cursor-default border border-[var(--admin-border)]'
              }`}
              title={unsavedChanges ? "Save changes" : "No changes to save"}
            >
              {/* Unsaved state */}
              <span className={`absolute inset-0 inline-flex items-center justify-center gap-1.5 transition-opacity duration-200 ${unsavedChanges ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Save
              </span>
              {/* Saved state */}
              <span className={`absolute inset-0 inline-flex items-center justify-center gap-1.5 transition-opacity duration-200 ${mobileSaveSuccess && !unsavedChanges ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Saved
              </span>
              {/* Idle state */}
              <span className={`absolute inset-0 inline-flex items-center justify-center transition-opacity duration-200 ${!unsavedChanges && !mobileSaveSuccess ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                Save
              </span>
            </button>
          </div>
        </div>

        {/* Edit content - Form owns scrolling internally; this wrapper just sizes it */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <FormComponent
            options={editorFormOptions}
            game={gameData}
            onOptionsUpdate={handleOptionsUpdate}
            onRequestPreviewReload={reloadPreview}
            params={params}
            validateForm={validateForm}
            errors={errors}
            formRef={formRef}
            navCollapsed={false}
            onSwitchGameType={handleSwitchGameType}
            allGameTypes={gameTypes}
          />
        </div>

        {/* Mobile Preview Drawer - iframe always mounted for live updates */}
        <AnimatePresence>
          {mobilePreviewOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                className="fixed inset-0 z-50 bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setMobilePreviewOpen(false)}
              />
              {/* Drawer chrome (header + close) - iframe is portalled in below */}
              <motion.div
                className="fixed bottom-0 left-0 right-0 z-50 flex flex-col overflow-hidden safe-area-top"
                style={{ background: 'var(--admin-bg-primary)', height: '100dvh' }}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              >
                {/* Compact header - handle + label + actions in one row */}
                <div className="shrink-0 flex items-center justify-between px-3 h-12 border-b border-white/[0.06]">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setMobilePreviewOpen(false)}
                      className="-m-2 p-2 inline-flex items-center justify-center"
                      aria-label="Close preview"
                    >
                      <span className="block w-10 h-1 rounded-full bg-white/30" />
                    </button>
                    <span className="text-xs font-semibold uppercase tracking-widest gch-text-muted">Preview</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg gch-text-secondary hover:gch-text-primary hover:bg-white/10 transition-colors"
                      title="Open in new tab"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                    </a>
                    <button
                      type="button"
                      onClick={() => setMobilePreviewOpen(false)}
                      className="p-2 rounded-lg gch-text-secondary hover:gch-text-primary hover:bg-white/10 transition-colors"
                      title="Close preview"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Preview iframe area - edge-to-edge */}
                <div className="flex-1 min-h-0 relative overflow-hidden">
                  {isPreviewLoading && (
                    <div className="absolute top-0 left-0 right-0 z-10 flex flex-col items-center">
                      <div className="w-full h-0.5 bg-white/10 overflow-hidden">
                        <div className="h-full bg-[var(--admin-accent)] animate-[preview-progress_1.2s_ease-in-out_infinite]" />
                      </div>
                      <div className="mt-2.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm flex items-center gap-2">
                        <ArrowPathIcon className="w-3.5 h-3.5 animate-spin text-[var(--admin-accent)]" />
                        <span className="text-[11px] font-medium gch-text-primary">Refreshing</span>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Mobile preview iframe - always mounted (hidden offscreen when drawer closed) for live postMessage updates */}
        <iframe
          key={`mobile-${iframeKey}`}
          ref={mobilePreviewIframeRef}
          src={`/p/${params.id}?preview=true`}
          className="border-0 block"
          style={{
            position: 'fixed',
            zIndex: mobilePreviewOpen ? 51 : -1,
            left: 0,
            right: 0,
            bottom: 0,
            top: mobilePreviewOpen ? 'auto' : '-9999px',
            height: mobilePreviewOpen ? 'calc(100dvh - 2.75rem)' : '1px',
            width: mobilePreviewOpen ? '100%' : '1px',
            pointerEvents: mobilePreviewOpen ? 'auto' : 'none',
            opacity: mobilePreviewOpen ? 1 : 0,
          }}
          title="Game Preview (mobile)"
          allowFullScreen
          onLoad={(e) => {
            setMobileIframeReady(true);
            handleIframeLoad(e);
          }}
        />
      </div>

      {/* Rename campaign modal */}
      <ModalBasic
        id="rename-campaign-modal"
        title="Rename campaign"
        modalOpen={renameModalOpen}
        setModalOpen={setRenameModalOpen}
      >
        <form onSubmit={handleRenameSubmit} className="p-6">
          <p className="gch-text-secondary text-[13px] leading-snug mb-4">
            This is your internal name for organising campaigns. Players don't see it.
          </p>
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Campaign name"
            autoFocus
            maxLength={120}
            className="w-full gch-input px-3 py-2 rounded-lg text-sm"
          />
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              className="btn-admin-outline btn-admin-md"
              onClick={() => setRenameModalOpen(false)}
              disabled={renameSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2.5 text-sm font-semibold rounded-[var(--admin-card-radius)] text-white bg-[var(--admin-cta-solid)] hover:brightness-110 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={renameSaving || !renameValue.trim()}
            >
              {renameSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </ModalBasic>

      {/* Unsaved Changes Modal */}
      <ModalBasic
        id="unsaved-changes-modal"
        title="Unsaved changes"
        modalOpen={showAlert}
        setModalOpen={handleModalConfirm}
      >
        <div className="p-6">
          <p className="gch-text-secondary text-[15px] leading-snug">
            Your changes have not been saved. Leave anyway?
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              className="btn-admin-outline btn-admin-md"
              onClick={() => setShowAlert(false)}
            >
              Stay
            </button>
            <button
              type="button"
              className="px-4 py-2.5 text-sm font-semibold rounded-[var(--admin-card-radius)] text-white bg-red-600 hover:bg-red-500 transition-colors"
              onClick={() => handleModalConfirm(false)}
            >
              Leave editor
            </button>
          </div>
        </div>
      </ModalBasic>

      {/* Game-type switch confirmation. Shown only when authored content
          (quiz questions, list items, custom holes) would be destroyed by
          the switch. Other warning-level impacts (custom artwork that
          doesn't translate) flip silently to keep the common path fast. */}
      <ModalBasic
        id="switch-game-type-modal"
        title="Switch game type?"
        modalOpen={!!pendingSwitchType && hasDestructiveImpact}
        setModalOpen={(open) => { if (!open) setPendingSwitchType(null); }}
      >
        <div className="p-6">
          <p className="gch-text-secondary text-[15px] leading-snug mb-4">
            Switching to <span className="font-semibold gch-text-primary">{pendingSwitchType?.name}</span> will delete the following from this campaign:
          </p>
          <ul className="space-y-2 mb-2">
            {switchImpact.map((item) => (
              <li
                key={item.kind}
                className={`flex items-start gap-2.5 text-sm ${item.severity === 'destructive' ? 'gch-text-primary' : 'gch-text-secondary'}`}
              >
                <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${item.severity === 'destructive' ? 'bg-red-400' : 'bg-amber-400'}`} />
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs gch-text-muted mt-4">Your branding, theme, share settings, and CTA will carry over.</p>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              className="btn-admin-outline btn-admin-md"
              onClick={() => setPendingSwitchType(null)}
            >
              Keep current type
            </button>
            <button
              type="button"
              className="px-4 py-2.5 text-sm font-semibold rounded-[var(--admin-card-radius)] text-white bg-red-600 hover:bg-red-500 transition-colors"
              onClick={async () => {
                const t = pendingSwitchType;
                setPendingSwitchType(null);
                if (t) await performSwitchGameType(t);
              }}
            >
              Switch and discard
            </button>
          </div>
        </div>
      </ModalBasic>

      <ToastContainer
        position="top-center"
        theme="dark"
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        limit={2}
      />
    </div>
  );
}
